import { Client, Room } from 'colyseus.js';
import type { DifficultyId, PlayerRole } from '@lucky-vr/common';
import { ClientMessage, ServerMessage } from '@lucky-vr/common';
import type {
  DriverInput,
  PassengerInput,
  ShotFiredPayload,
  HitConfirmPayload,
  EnemyDestroyedPayload,
  EnemyTelegraphPayload,
  EnemyFiredPayload,
  BossPhaseChangePayload,
  ObstacleDestroyedPayload,
  StageBriefingPayload,
  ObjectiveChangedPayload,
  TargetStatePayload,
  TargetConfigurationPayload,
  TargetComponentBrokenPayload,
  HazardPayload,
  StageDebriefPayload,
  BeatRecoveryPayload,
} from '@lucky-vr/common';

/**
 * Where the game server lives:
 *  - VITE_SERVER_URL wins if set at build time,
 *  - dev (localhost) talks straight to the Colyseus port,
 *  - production connects same-origin under /game, which nginx proxies
 *    (see etc/nginx/lucky-vr.conf).
 */
function defaultServerUrl(): string {
  const fromEnv = import.meta.env?.VITE_SERVER_URL as string | undefined;
  if (fromEnv) return fromEnv;
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return 'ws://localhost:2567';
  }
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/game`;
}

const DEFAULT_SERVER = defaultServerUrl();

export class NetworkManager {
  private client: Client;
  private gameRoom: Room | null = null;
  private joinInFlight: Promise<Room> | null = null;

  // Callbacks
  onStateChange: ((state: any) => void) | null = null;
  onRoleAssigned: ((role: PlayerRole) => void) | null = null;
  onCountdown: ((count: number, level: number) => void) | null = null;
  onGameStart: ((level: number) => void) | null = null;
  onStageBriefing: ((data: StageBriefingPayload) => void) | null = null;
  onObjectiveChanged: ((data: ObjectiveChangedPayload) => void) | null = null;
  onBeatRecovery: ((data: BeatRecoveryPayload) => void) | null = null;
  onTargetContact: ((data: TargetStatePayload) => void) | null = null;
  onTargetStateChanged: ((data: TargetStatePayload) => void) | null = null;
  onTargetConfigurationChanged: ((data: TargetConfigurationPayload) => void) | null = null;
  onTargetComponentBroken: ((data: TargetComponentBrokenPayload) => void) | null = null;
  onHazardWarning: ((data: HazardPayload) => void) | null = null;
  onHazardCommitted: ((data: HazardPayload) => void) | null = null;
  onHazardResolved: ((data: HazardPayload) => void) | null = null;
  onStageDebrief: ((data: StageDebriefPayload) => void) | null = null;
  onCampaignComplete: ((data: StageDebriefPayload) => void) | null = null;
  onShotFired: ((data: ShotFiredPayload) => void) | null = null;
  onHitConfirm: ((data: HitConfirmPayload) => void) | null = null;
  onEnemyDestroyed: ((data: EnemyDestroyedPayload) => void) | null = null;
  onEnemyTelegraph: ((data: EnemyTelegraphPayload) => void) | null = null;
  onEnemyFired: ((data: EnemyFiredPayload) => void) | null = null;
  onBossPhaseChange: ((data: BossPhaseChangePayload) => void) | null = null;
  onObstacleDestroyed: ((data: ObstacleDestroyedPayload) => void) | null = null;
  onLevelComplete: ((data: { level: number; bossEscaped?: boolean }) => void) | null = null;
  onGameOver: ((data: { reason: string }) => void) | null = null;
  onPartnerDisconnected: (() => void) | null = null;
  onPartnerReconnected: (() => void) | null = null;

  constructor(serverUrl: string = DEFAULT_SERVER) {
    this.client = new Client(serverUrl);
  }

  get sessionId(): string | null {
    return this.gameRoom?.sessionId ?? null;
  }

  async joinSolo(startLevel?: number, difficulty?: DifficultyId): Promise<Room> {
    return this.joinOnce(() => this.client.create('game', { solo: true, startLevel, difficulty }));
  }

  /** Public matchmaking: land in any open public room, or open a new one. */
  async joinRandom(startLevel?: number, difficulty?: DifficultyId): Promise<Room> {
    return this.joinOnce(() => this.client.joinOrCreate('game', { mode: 'public', startLevel, difficulty }));
  }

  /** Host a private lobby under a share code. */
  async createLobby(code: string, startLevel?: number, difficulty?: DifficultyId): Promise<Room> {
    return this.joinOnce(() => this.client.create('game', { mode: 'private', code, startLevel, difficulty }));
  }

  /** Join a friend's private lobby by its share code. Throws if none exists. */
  async joinByCode(code: string): Promise<Room> {
    return this.joinOnce(() => this.client.join('game', { mode: 'private', code }));
  }

  /** One client may own only one room. Repeated VR trigger presses share the same attempt. */
  private joinOnce(connect: () => Promise<Room>): Promise<Room> {
    if (this.gameRoom) return Promise.resolve(this.gameRoom);
    if (this.joinInFlight) return this.joinInFlight;

    this.joinInFlight = Promise.resolve()
      .then(connect)
      .then((room) => {
        this.gameRoom = room;
        this.setupGameRoomListeners();
        return room;
      })
      .finally(() => {
        this.joinInFlight = null;
      });
    return this.joinInFlight;
  }

  sendSelectRole(role: 'driver' | 'passenger' | 'random'): void {
    this.gameRoom?.send(ClientMessage.SelectRole, { role });
  }

  private setupGameRoomListeners(): void {
    if (!this.gameRoom) return;

    const room = this.gameRoom;
    room.onStateChange((state) => this.onStateChange?.(state));

    room.onMessage(ServerMessage.RoleAssigned, (d: { role: PlayerRole }) => this.onRoleAssigned?.(d.role));
    room.onMessage(ServerMessage.CountdownTick, (d: { count: number; level: number }) => this.onCountdown?.(d.count, d.level));
    room.onMessage(ServerMessage.GameStart, (d: { level: number }) => this.onGameStart?.(d.level));
    room.onMessage(ServerMessage.StageBriefing, (d: StageBriefingPayload) => this.onStageBriefing?.(d));
    room.onMessage(ServerMessage.ObjectiveChanged, (d: ObjectiveChangedPayload) => this.onObjectiveChanged?.(d));
    room.onMessage(ServerMessage.BeatRecovery, (d: BeatRecoveryPayload) => this.onBeatRecovery?.(d));
    room.onMessage(ServerMessage.TargetContact, (d: TargetStatePayload) => this.onTargetContact?.(d));
    room.onMessage(ServerMessage.TargetStateChanged, (d: TargetStatePayload) => this.onTargetStateChanged?.(d));
    room.onMessage(ServerMessage.TargetConfigurationChanged, (d: TargetConfigurationPayload) => this.onTargetConfigurationChanged?.(d));
    room.onMessage(ServerMessage.TargetComponentBroken, (d: TargetComponentBrokenPayload) => this.onTargetComponentBroken?.(d));
    room.onMessage(ServerMessage.HazardWarning, (d: HazardPayload) => this.onHazardWarning?.(d));
    room.onMessage(ServerMessage.HazardCommitted, (d: HazardPayload) => this.onHazardCommitted?.(d));
    room.onMessage(ServerMessage.HazardResolved, (d: HazardPayload) => this.onHazardResolved?.(d));
    room.onMessage(ServerMessage.StageDebrief, (d: StageDebriefPayload) => this.onStageDebrief?.(d));
    room.onMessage(ServerMessage.CampaignComplete, (d: StageDebriefPayload) => this.onCampaignComplete?.(d));
    room.onMessage(ServerMessage.ShotFired, (d: ShotFiredPayload) => this.onShotFired?.(d));
    room.onMessage(ServerMessage.HitConfirm, (d: HitConfirmPayload) => this.onHitConfirm?.(d));
    room.onMessage(ServerMessage.EnemyDestroyed, (d: EnemyDestroyedPayload) => this.onEnemyDestroyed?.(d));
    room.onMessage(ServerMessage.EnemyTelegraph, (d: EnemyTelegraphPayload) => this.onEnemyTelegraph?.(d));
    room.onMessage(ServerMessage.EnemyFired, (d: EnemyFiredPayload) => this.onEnemyFired?.(d));
    room.onMessage(ServerMessage.BossPhaseChange, (d: BossPhaseChangePayload) => this.onBossPhaseChange?.(d));
    room.onMessage(ServerMessage.ObstacleDestroyed, (d: ObstacleDestroyedPayload) => this.onObstacleDestroyed?.(d));
    room.onMessage(ServerMessage.LevelComplete, (d: { level: number; bossEscaped?: boolean }) => this.onLevelComplete?.(d));
    room.onMessage(ServerMessage.GameOver, (d: { reason: string }) => this.onGameOver?.(d));
    room.onMessage(ServerMessage.PlayerDisconnected, () => this.onPartnerDisconnected?.());
    room.onMessage(ServerMessage.PlayerReconnected, () => this.onPartnerReconnected?.());
  }

  sendDriverInput(input: DriverInput): void {
    this.gameRoom?.send(ClientMessage.DriverInput, input);
  }

  sendPassengerInput(input: PassengerInput): void {
    this.gameRoom?.send(ClientMessage.PassengerInput, input);
  }

  sendReady(): void {
    this.gameRoom?.send(ClientMessage.PlayerReady, {});
  }

  sendSkipPresentation(): void {
    this.gameRoom?.send(ClientMessage.SkipPresentation, {});
  }

  disconnect(): void {
    this.gameRoom?.leave();
    this.gameRoom = null;
    this.joinInFlight = null;
  }
}
